import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ExportCapabilities = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const navigate = useNavigate();

  const exportSteps = [
    {
      id: 1,
      title: "Product Sourcing",
      description: "Identify and verify premium African suppliers based on your specific requirements and quality standards.",
      icon: "Search",
      duration: "1-3 days",
      details: [
        "Supplier verification and vetting",
        "Product quality assessment",
        "Pricing negotiation",
        "Sample procurement"
      ],
      status: "completed"
    },
    {
      id: 2,
      title: "Quality Assurance",
      description: "Comprehensive quality control including pre-shipment inspections and certification processes.",
      icon: "Shield",
      duration: "2-4 days",
      details: [
        "ISO-certified quality checks",
        "Pre-shipment inspection",
        "Quality documentation",
        "Compliance verification"
      ],
      status: "completed"
    },
    {
      id: 3,
      title: "Documentation",
      description: "Complete export documentation including certificates of origin, quality certificates, and customs paperwork.",
      icon: "FileText",
      duration: "1-2 days",
      details: [
        "Export licenses and permits",
        "Certificate of origin",
        "Quality certificates",
        "Customs documentation"
      ],
      status: "in-progress"
    },
    {
      id: 4,
      title: "Packaging & Labeling",
      description: "Professional packaging designed for international shipping with proper labeling and handling instructions.",
      icon: "Package",
      duration: "1-2 days",
      details: [
        "Export-grade packaging",
        "International labeling",
        "Handling instructions",
        "Tracking preparation"
      ],
      status: "pending"
    },
    {
      id: 5,
      title: "Shipping & Logistics",
      description: "Coordinated shipping through our global logistics network with real-time tracking and insurance coverage.",
      icon: "Truck",
      duration: "14-21 days",
      details: [
        "Freight coordination",
        "Customs clearance",
        "Real-time tracking",
        "Insurance coverage"
      ],
      status: "pending"
    },
    {
      id: 6,
      title: "Delivery & Support",
      description: "Final delivery confirmation with post-delivery support and feedback collection for continuous improvement.",
      icon: "CheckCircle",
      duration: "Ongoing",
      details: [
        "Delivery confirmation",
        "Quality feedback",
        "Post-delivery support",
        "Relationship management"
      ],
      status: "pending"
    }
  ];

  const handleStepChange = (newStep) => {
    setActiveStep(newStep);
    setIsUserInteracting(true);
    scrollToStep(newStep);
  };

  const scrollToStep = (stepIndex) => {
    // Scroll the timeline to show the active step
    if (timelineRef.current) {
      // Get the actual card element to measure its real dimensions
      const cards = timelineRef.current.children;
      if (cards[stepIndex]) {
        const targetCard = cards[stepIndex];
        const containerWidth = timelineRef.current.clientWidth;
        const cardRect = targetCard.getBoundingClientRect();
        const containerRect = timelineRef.current.getBoundingClientRect();
        
        // Calculate the position of the card relative to the container
        const cardLeft = targetCard.offsetLeft;
        const cardWidth = targetCard.offsetWidth;
        
        // Calculate scroll position to center the card
        const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
        
        timelineRef.current.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  };

  // Auto-advance only when user is not interacting
  useEffect(() => {
    if (!isUserInteracting) {
    const interval = setInterval(() => {
      setActiveStep((prev) => {
        const newStep = (prev + 1) % exportSteps.length;
        scrollToStep(newStep);
        return newStep;
      });
      }, 4000); // Increased to 4 seconds for better UX

    return () => clearInterval(interval);
    }
  }, [isUserInteracting, exportSteps.length]);

  // Reset user interaction flag after 10 seconds
  useEffect(() => {
    if (isUserInteracting) {
      const timeout = setTimeout(() => {
        setIsUserInteracting(false);
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [isUserInteracting]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success';
      case 'in-progress': return 'text-accent bg-accent';
      case 'pending': return 'text-text-muted bg-surface-300';
      default: return 'text-text-muted bg-surface-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in-progress': return 'Clock';
      case 'pending': return 'Circle';
      default: return 'Circle';
    }
  };

  return (
    <section className="py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Timeline Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-text-secondary">Process Timeline</span>
            <span className="text-sm font-medium text-text-secondary">Total: 19-32 days</span>
          </div>
          <div className="relative">
            <div className="h-2 bg-surface-200 rounded-full"></div>
            <motion.div
              className="absolute top-0 left-0 h-2 bg-gradient-to-r from-primary to-accent rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((activeStep + 1) / exportSteps.length) * 100}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            ></motion.div>
          </div>
        </div>

        {/* Horizontal Scrolling Timeline */}
        <div className="relative">
          {/* Scroll Indicators for better UX */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-text-secondary">Our Export Process Timeline</p>
            <div className="flex items-center space-x-2 text-xs text-text-secondary">
              <div className="hidden sm:flex items-center space-x-2">
                <Icon name="ArrowLeft" size={16} />
                <span>Scroll to explore steps</span>
                <Icon name="ArrowRight" size={16} />
              </div>
              <div className="sm:hidden flex items-center space-x-1">
                <Icon name="Smartphone" size={14} />
                <span>Swipe to explore</span>
              </div>
            </div>
          </div>
          
          <div 
            ref={timelineRef}
            className="flex space-x-4 sm:space-x-6 lg:space-x-8 py-4 px-4 overflow-x-auto scroll-smooth"
            style={{ 
              scrollSnapType: 'x mandatory', 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#10b981 #f3f4f6'
            }}
          >
            {exportSteps.map((step, index) => (
              <motion.div
                key={step.id}
                className={`flex-shrink-0 w-80 sm:w-96 lg:w-80 glass-card p-4 sm:p-6 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  activeStep === index ? 'ring-2 ring-primary shadow-elevation-lg scale-[1.01]' : 'hover:scale-[1.005]'
                }`}
                style={{ scrollSnapAlign: 'center' }}
                onClick={() => handleStepChange(index)}
                whileHover={{ scale: activeStep === index ? 1.01 : 1.005 }}
                whileTap={{ scale: 0.995 }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStepChange(index);
                  }
                }}
              >
                {/* Step Header */}
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      activeStep >= index ? 'bg-primary text-white' : 'bg-surface-200 text-text-muted'
                    }`}>
                      <Icon name={step.icon} size={20} className="sm:w-6 sm:h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-primary text-sm sm:text-base truncate">{step.title}</h3>
                      <p className="text-xs sm:text-sm text-text-secondary">{step.duration}</p>
                    </div>
                  </div>
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${getStatusColor(step.status)}`}>
                    <Icon name={getStatusIcon(step.status)} size={14} className="sm:w-4 sm:h-4" color="white" />
                  </div>
                </div>

                {/* Step Description */}
                <p className="text-text-secondary mb-3 sm:mb-4 text-xs sm:text-sm leading-relaxed">
                  {step.description}
                </p>

                {/* Step Details */}
                <div className="space-y-1.5 sm:space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full mt-1.5 flex-shrink-0"></div>
                      <span className="text-xs text-text-secondary leading-tight">{detail}</span>
                    </div>
                  ))}
                </div>

                {/* Progress Indicator */}
                {activeStep === index && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="mt-4 h-1 bg-gradient-to-r from-primary to-accent rounded-full"
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center space-x-4 mt-6 sm:mt-8">
            <button
              onClick={() => handleStepChange(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              className="p-3 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-250 disabled:opacity-30 disabled:cursor-not-allowed border border-surface-200"
              aria-label="Previous step"
            >
              <Icon name="ChevronLeft" size={20} className="text-primary" />
            </button>
            
            <div className="flex space-x-2">
              {exportSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStepChange(index)}
                  className={`h-3 rounded-full transition-all duration-250 hover:scale-110 ${
                    activeStep === index ? 'bg-primary w-8' : 'bg-surface-300 w-3 hover:bg-surface-400'
                  }`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
            
            <button
              onClick={() => handleStepChange(Math.min(exportSteps.length - 1, activeStep + 1))}
              disabled={activeStep === exportSteps.length - 1}
              className="p-3 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-250 disabled:opacity-30 disabled:cursor-not-allowed border border-surface-200"
              aria-label="Next step"
            >
              <Icon name="ChevronRight" size={20} className="text-primary" />
            </button>
          </div>

          {/* Auto-advance indicator */}
          {!isUserInteracting && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute top-4 right-4 flex items-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-text-secondary"
            >
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
              <span>Auto-advancing</span>
            </motion.div>
          )}
        </div>

        {/* Current Step Details */}
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mt-12 glass-card p-8"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                  <Icon name={exportSteps[activeStep].icon} size={32} color="white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-primary">{exportSteps[activeStep].title}</h3>
                  <p className="text-text-secondary">Step {activeStep + 1} of {exportSteps.length}</p>
                </div>
              </div>
              <p className="text-text-secondary mb-6 leading-relaxed">
                {exportSteps[activeStep].description}
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-accent">{exportSteps[activeStep].duration}</span>
                </div>
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
                  exportSteps[activeStep].status === 'completed' ? 'bg-success-100 text-success' :
                  exportSteps[activeStep].status === 'in-progress'? 'bg-accent-100 text-accent' : 'bg-surface-200 text-text-muted'
                }`}>
                  <Icon name={getStatusIcon(exportSteps[activeStep].status)} size={12} />
                  <span className="capitalize">{exportSteps[activeStep].status.replace('-', ' ')}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-semibold text-primary mb-4">Key Activities:</h4>
              {exportSteps[activeStep].details.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center space-x-3 p-3 bg-surface-100 rounded-lg"
                >
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="text-text-primary">{detail}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2, ease: "easeOut" }}
          className="mt-12 text-center"
        >
          <h3 className="text-2xl font-bold text-primary mb-4">
            Ready to Start Your Export Journey?
          </h3>
          <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
            Our expert team will guide you through each step, ensuring a smooth and successful export experience from start to finish.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => navigate('/contact')}
              className="btn-accent px-8 py-4 font-semibold flex items-center space-x-2"
            >
              <Icon name="Play" size={20} />
              <span>Start Export Process</span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExportCapabilities;